import { useContext, useMemo, type ReactNode } from "react";
import {
  analyticsScopeContexts,
  getAnalyticsScopeContext,
} from "./scopeContexts.js";
import type {
  AnalyticsContexts,
  AnalyticsProviderValue,
  AnalyticsScopeName,
} from "./scopes.js";

interface AnalyticsProviderProps<K extends AnalyticsScopeName> {
  scope: K;
  /** A complete scope value, or a pure builder with access to all parent scopes. */
  value: AnalyticsProviderValue<K>;
  children: ReactNode;
}

/**
 * Providers need the full parent registry so value builders can derive a new
 * scope. Named consumers use a separate hook to subscribe to only one scope.
 */
function useParentAnalyticsContexts(): AnalyticsContexts {
  const teammatePage = useContext(analyticsScopeContexts.teammatePage);
  const coaching = useContext(analyticsScopeContexts.coaching);

  return useMemo(
    () => ({
      ...(teammatePage === undefined ? {} : { teammatePage }),
      ...(coaching === undefined ? {} : { coaching }),
    }),
    [coaching, teammatePage],
  );
}

/**
 * Adds or shadows one flat, typed scope while preserving every parent scope.
 * A value factory sees the parent context before this provider is applied.
 */
export const AnalyticsProvider = <K extends AnalyticsScopeName>(
  props: AnalyticsProviderProps<K>,
) => {
  const { scope, value, children } = props;
  const parent = useParentAnalyticsContexts();
  const scopeValue = useMemo(
    () => (typeof value === "function" ? value(parent) : value),
    [parent, value],
  );
  const ScopeContext = getAnalyticsScopeContext(scope);

  return <ScopeContext value={scopeValue}>{children}</ScopeContext>;
};
